import { FlightCore } from "../flight";
import { SkyscannerPlace } from "./suggest";


export namespace SkyscannerResultsV3 {

  export enum ResultAction {
    Replaced = 'RESULT_ACTION_REPLACED',
    NotModified = 'RESULT_ACTION_NOT_MODIFIED',
    Omitted = 'RESULT_ACTION_OMITTED',
    Default = 'RESULT_ACTION_UNSPECIFIED',
  }

  export enum ResultStatus {
    Complete = 'RESULT_STATUS_COMPLETE',
    Incomplete = 'RESULT_STATUS_INCOMPLETE',
    Failed = 'RESULT_STATUS_FAILED',
    Default = 'RESULT_STATUS_UNSPECIFIED',
  }

  export enum PriceUnit {
    Whole = 'PRICE_UNIT_WHOLE',
    Centi = 'PRICE_UNIT_CENTI',
    Milli = 'PRICE_UNIT_MILLI',
    Micro = 'PRICE_UNIT_MICRO',
    Default = 'PRICE_UNIT_UNSPECIFIED'
  }

  export enum PriceUpdateStatus {
    Pending = 'PRICE_UPDATE_STATUS_PENDING',
    Current = 'PRICE_UPDATE_STATUS_CURRENT',
    Default = 'PRICE_UPDATE_STATUS_UNSPECIFIED',
  }

  export enum TransferType {
    Managed = 'TRANSFER_TYPE_MANAGED',
    Self = 'TRANSFER_TYPE_SELF_TRANSFER',
    ProtectedSelf = 'TRANSFER_TYPE_PROTECTED_SELF_TRANSFER',
    Default = 'TRANSFER_TYPE_UNSPECIFIED',
  }

  export enum AgentType {
    TravelAgent = 'AGENT_TYPE_TRAVEL_AGENT',
    Airline = 'AGENT_TYPE_AIRLINE',
    Default = 'AGENT_TYPE_UNSPECIFIED',
  }

  export interface LivePricingResponse {
    sessionToken: string;
    status: ResultStatus;
    action: ResultAction;
    content: Content;
  }

  export interface Content {
    results: Results;
    sortingOptions: SortingOptions;
  }

  export interface Results {
    itineraries: { [key: string]: Itinerary; };
    legs: { [key: string]: Leg; };
    segments: { [key: string]: Segment; };
    places: { [key: string]: Partial<SkyscannerPlace> & { iata?: string }; };
    carriers: { [key: string]: Carrier; };
    agents: { [key: string]: Agent; };
    alliances: { [key: string]: Alliance; };
  }

  export interface Itinerary {
    legIds: string[];
    pricingOptions: PricingOption[];
    sustainabilityData?: SustainabilityData;
  }

  export interface PricingOption {
    agentIds: string[];
    price: Price;
    id: string;
    items: PriceItems[];
    transferType: TransferType;
  }

  export interface PriceItems {
    agentId: string;
    deepLink: string;
    fares: Fares[];
    price: Price;
  }

  export interface Price {
    amount: string;
    unit: PriceUnit;
    updateStatus: PriceUpdateStatus;
  }

  export interface Fares {
    bookingCode: string;
    fareBasisCode: string;
    segmentId: string;
  }

  export interface SustainabilityData {
    isEcoContender: boolean;
    ecoContenderDelta: number;
  }

  export interface Leg {
    originPlaceId: string;
    destinationPlaceId: string;
    departureDateTime: DateType;
    arrivalDateTime: DateType;
    durationInMinutes: number;
    stopCount: number;
    marketingCarrierIds: string[];
    operatingCarrierIds: string[];
    segmentIds: string[];
  }

  export interface Segment {
    originPlaceId: string;
    destinationPlaceId: string;
    departureDateTime: DateType;
    arrivalDateTime: DateType;
    durationInMinutes: number;
    marketingCarrierIds: string;
    operatingCarrierIds: string;
  }

  export interface DateType {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
  }

  export interface Carrier {
    name: string;
    allianceId?: string;
    imageUrl?: string;
    iata?: string;
  }

  export interface Agent {
    name: string;
    type: AgentType;
    imageUrl?: string;
  }

  export interface Alliance {
    name: string;
  }

  export interface SortingOptions {
    best: SortingOption[];
    cheapest: SortingOption[];
    fastest: SortingOption[];
  }

  export interface SortingOption {
    itineraryId: string;
    score: number;
  }

  export interface Stats {
    itineraries: {
      hasChangeAirportTransfer: boolean;
      minDuration: number;
      maxDuration: number;
    }
  }

  export type QuotesTyped = { [key: string]: Quotes }

  export interface IndicativeSearchResponse {
    status: ResultStatus;
    content: {
      results: {
        quotes: QuotesTyped;
      }
      groupingOptions: {
        byRoute: {
          quotesGroups: {
            quoteIds: string[];
          }[]
        },
        byDate: {
          quotesInboundGroups: DatesGroup[],
          quotesOutboundGroups: DatesGroup[],
        }
      }
    }
  }

  export type DateTypeShort = Pick<DateType, 'year' | 'month' | 'day'>

  export interface DatesGroup {
    monthYearDate: DateTypeShort,
    quoteIds: string[];
  }

  export interface Quotes {
    minPrice: Price;
    isDirect: boolean;
    inboundLeg?: IndicativeLeg;
    outboundLeg: IndicativeLeg;
  }

  export interface IndicativeLeg {
    departureDateTime: DateType;
    originPlace: PlaceShort & {city: string};
    destinationPlace: PlaceShort & {
      city: string, 
      image?: Image,
    };
    
  }


  export interface DateWithQuotes {
    quotes: SkyscannerResultsV3.Quotes[];
    date: SkyscannerResultsV3.DateTypeShort;
    direction: FlightCore.Directionality;
  }

  export interface ResizeUrl {
    230: string;
  }

  export interface Image {
      url: string;
      resizeUrl: ResizeUrl;
  }


  export type PlaceShort = Pick<SkyscannerPlace, 'entityId' | 'name' | 'parentId' | 'type'> & { 
    iata?: string
  }; 

  export interface NearestPlacesResponse {
    nearestPlace: PlaceShort;
    destinations: Quotes[];
  }

}